import { Order, Customer, Product, Category, OrderItem } from '../types';
import { query, testConnection } from './dbService';
import { generateEasternTimestamp } from '../utils';

// Browser-compatible UUID generation function
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Initialize Storage
export const initStorage = async (): Promise<boolean> => {
  try {
    // Test database connection
    const isConnected = await testConnection();
    if (!isConnected) {
      console.error('Failed to connect to database. Check your database configuration.');
      return false;
    }

    console.log('Database connection successful. Storage service initialized.');
    return true;
  } catch (error) {
    console.error('Error initializing storage:', error);
    return false;
  }
};

// Get order summaries with items but without status history
export const getOrderSummaries = async (): Promise<Order[]> => {
  try {
    const orders = await query<Order[]>(`
      SELECT * FROM orders
      ORDER BY createdAt DESC
    `);

    // Check if orders is defined and has elements
    if (orders && Array.isArray(orders)) {
      // Fetch all order items in a single query
      const allOrderItems = await query<OrderItem[]>(`
        SELECT * FROM orderItems
      `);

      // Create a map of order IDs to their items for quick lookup
      const orderItemsMap: Record<string, OrderItem[]> = {};
      if (allOrderItems && Array.isArray(allOrderItems)) {
        allOrderItems.forEach(item => {
          if (item && item.orderId) {
            if (!orderItemsMap[item.orderId]) {
              orderItemsMap[item.orderId] = [];
            }
            orderItemsMap[item.orderId].push(item);
          }
        });
      }

      // Assign items to each order
      for (const order of orders) {
        if (!order || typeof order !== 'object' || !order.id) {
          console.error('Invalid order object:', order);
          continue;
        }

        // Set items from the map or empty array if none found
        order.items = orderItemsMap[order.id] || [];

        // Still initialize status history and takenBy without fetching
        order.statusHistory = [];
        order.takenBy = 'Unknown';
      }
    }

    return Array.isArray(orders) ? orders : [];
  } catch (error) {
    console.error('Error fetching order summaries:', error);
    return [];
  }
};

// Get full details for a specific order
export const getOrderDetails = async (orderId: string): Promise<Order | null> => {
  try {
    const orders = await query<Order[]>(`
      SELECT * FROM orders
      WHERE id = ?
    `, [orderId]);

    if (!orders || !Array.isArray(orders) || orders.length === 0) {
      return null;
    }

    const order = orders[0];

    try {
      // Fetch order items
      const items = await query<OrderItem[]>(`
        SELECT * FROM orderItems
        WHERE orderId = ?
      `, [order.id]);

      order.items = Array.isArray(items) ? items : [];

      // Fetch status history
      const statusHistory = await query(`
        SELECT * FROM statusHistory
        WHERE orderId = ?
        ORDER BY timestamp
      `, [order.id]);

      order.statusHistory = Array.isArray(statusHistory) ? statusHistory : [];

      // Set takenBy field based on statusHistory
      if (Array.isArray(statusHistory) && statusHistory.length > 0) {
        // Find the first status entry (chronologically) to determine who took the order
        const firstStatusEntry = statusHistory[0]; // statusHistory is already ordered by timestamp
        order.takenBy = firstStatusEntry.updatedBy;
      } else {
        order.takenBy = 'Unknown';
      }
    } catch (itemError) {
      console.error(`Error fetching details for order ${order.id}:`, itemError);
      order.items = [];
      order.statusHistory = [];
      order.takenBy = 'Unknown';
    }

    return order;
  } catch (error) {
    console.error('Error fetching order details:', error);
    return null;
  }
};

// For backward compatibility
export const getOrders = async (): Promise<Order[]> => {
  try {
    const orders = await query<Order[]>(`
      SELECT * FROM orders
      ORDER BY createdAt DESC
    `);

    // Check if orders is defined and has elements
    if (orders && Array.isArray(orders)) {
      // Fetch order items for each order
      for (const order of orders) {
        if (!order || typeof order !== 'object' || !order.id) {
          console.error('Invalid order object:', order);
          continue;
        }

        try {
          const items = await query<OrderItem[]>(`
            SELECT * FROM orderItems
            WHERE orderId = ?
          `, [order.id]);

          order.items = Array.isArray(items) ? items : [];

          // Fetch status history
          const statusHistory = await query(`
            SELECT * FROM statusHistory
            WHERE orderId = ?
            ORDER BY timestamp
          `, [order.id]);

          order.statusHistory = Array.isArray(statusHistory) ? statusHistory : [];

          // Set takenBy field based on statusHistory
          if (Array.isArray(statusHistory) && statusHistory.length > 0) {
            // Find the first status entry (chronologically) to determine who took the order
            const firstStatusEntry = statusHistory[0]; // statusHistory is already ordered by timestamp
            order.takenBy = firstStatusEntry.updatedBy;
          } else {
            order.takenBy = 'Unknown';
          }
        } catch (itemError) {
          console.error(`Error fetching details for order ${order.id}:`, itemError);
          order.items = [];
          order.statusHistory = [];
          order.takenBy = 'Unknown';
        }
      }
    }

    return Array.isArray(orders) ? orders : [];
  } catch (error) {
    console.error('Error fetching orders:', error);
    return [];
  }
};

export const saveOrder = async (order: Order): Promise<void> => {
  // Validate order object
  if (!order || typeof order !== 'object') {
    console.error('Invalid order object:', order);
    throw new Error('Invalid order object');
  }

  if (!order.id) {
    console.error('Order missing ID:', order);
    throw new Error('Order missing ID');
  }

  // Generate current timestamp in Eastern timezone
  const currentTimestamp = generateEasternTimestamp();

  try {
    // Check if order exists
    const existingOrder = await query<Order[]>(`
      SELECT * FROM orders WHERE id = ?
    `, [order.id]);

    const isUpdate = existingOrder && Array.isArray(existingOrder) && existingOrder.length > 0;

    if (isUpdate) {
      // Update existing order with server-generated timestamp
      await query(`
        UPDATE orders
        SET customerId = ?, firstName = ?, lastName = ?, phone = ?, email = ?,
            pickupDate = ?, pickupTime = ?, status = ?, location = ?,
            total = ?, lastUpdated = ?, notes = ?
        WHERE id = ?
      `, [
        order.customerId || null, order.firstName, order.lastName, order.phone, order.email,
        order.pickupDate, order.pickupTime, order.status, order.location,
        order.total, currentTimestamp, order.notes, order.id
      ]);

      // Update the order object with the new timestamp
      order.lastUpdated = currentTimestamp;
    } else {
      // Insert new order with server-generated timestamps
      await query(`
        INSERT INTO orders
        (id, customerId, firstName, lastName, phone, email, pickupDate, pickupTime,
         status, location, total, createdAt, lastUpdated, notes)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `, [
        order.id, order.customerId || null, order.firstName, order.lastName, order.phone, order.email,
        order.pickupDate, order.pickupTime, order.status, order.location,
        order.total, currentTimestamp, currentTimestamp, order.notes
      ]);

      // Update the order object with the new timestamps
      order.createdAt = currentTimestamp;
      order.lastUpdated = currentTimestamp;
    }

    // Handle order items - delete existing and insert new
    await query(`DELETE FROM orderItems WHERE orderId = ?`, [order.id]);

    // Validate order items
    if (order.items && Array.isArray(order.items)) {
      for (const item of order.items) {
        if (!item || typeof item !== 'object') {
          console.error('Invalid order item:', item);
          continue;
        }

        if (!item.id) {
          console.error('Order item missing ID:', item);
          continue;
        }

        try {
          await query(`
            INSERT INTO orderItems
            (id, orderId, productId, name, price, quantity, isDone, notes)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?)
          `, [
            item.id, order.id, item.productId || '', item.name || '',
            item.price || 0, item.quantity || 1, item.isDone || false, item.notes || null
          ]);
        } catch (itemError) {
          console.error(`Error inserting order item ${item.id}:`, itemError);
        }
      }
    } else {
      console.error('Order items is not an array:', order.items);
    }

    // Handle status history - add new status if it changed
    if (order.statusHistory && Array.isArray(order.statusHistory) && order.statusHistory.length > 0) {
      try {
        const latestStatus = order.statusHistory[order.statusHistory.length - 1];

        if (!latestStatus || typeof latestStatus !== 'object') {
          console.error('Invalid status history entry:', latestStatus);
        } else if (!latestStatus.status) {
          console.error('Status history entry missing required fields:', latestStatus);
        } else {
          // Always use server-generated timestamp in Eastern timezone
          const statusTimestamp = currentTimestamp;

          // Check if this status already exists (only check by orderId and status, not timestamp)
          const existingStatus = await query(`
            SELECT * FROM statusHistory
            WHERE orderId = ? AND status = ?
            ORDER BY timestamp DESC LIMIT 1
          `, [order.id, latestStatus.status]);

          // Only add if status doesn't exist or if it's a different timestamp (to avoid duplicates)
          if (!existingStatus || !Array.isArray(existingStatus) || existingStatus.length === 0 ||
              (existingStatus[0].timestamp !== statusTimestamp)) {
            await query(`
              INSERT INTO statusHistory
              (orderId, status, timestamp, updatedBy)
              VALUES (?, ?, ?, ?)
            `, [
              order.id, latestStatus.status, statusTimestamp, latestStatus.updatedBy || 'System'
            ]);

            // Update the status history entry with the new timestamp
            latestStatus.timestamp = statusTimestamp;
          }
        }
      } catch (statusError) {
        console.error('Error handling status history:', statusError);
      }
    }

    // Update customer information
    await updateCustomerFromOrder(order);
  } catch (error) {
    console.error('Error saving order:', error);
    throw error;
  }
};

export const getCustomers = async (): Promise<Customer[]> => {
  try {
    // First get all customers
    const customers = await query<Customer[]>(`
      SELECT * FROM customers
      ORDER BY lastName, firstName
    `);

    if (!customers || !Array.isArray(customers)) {
      return [];
    }

    // Get all order counts in a single query using customerId if available
    const orderCounts = await query<{customerId: string, count: number}[]>(`
      SELECT customerId, COUNT(*) as count 
      FROM orders 
      WHERE customerId IS NOT NULL
      GROUP BY customerId
    `);

    // Create a map of customer IDs to order counts for quick lookup
    const customerIdToCountMap: Record<string, number> = {};
    if (orderCounts && Array.isArray(orderCounts)) {
      orderCounts.forEach(row => {
        if (row && row.customerId) {
          customerIdToCountMap[row.customerId] = row.count;
        }
      });
    }

    // For backward compatibility, also get counts by phone for orders without customerId
    const phoneOrderCounts = await query<{phone: string, count: number}[]>(`
      SELECT phone, COUNT(*) as count 
      FROM orders 
      WHERE customerId IS NULL
      GROUP BY phone
    `);

    // Create a map of phone numbers to order counts for quick lookup
    const phoneToCountMap: Record<string, number> = {};
    if (phoneOrderCounts && Array.isArray(phoneOrderCounts)) {
      phoneOrderCounts.forEach(row => {
        if (row && row.phone) {
          phoneToCountMap[row.phone] = row.count;
        }
      });
    }

    // Update each customer with their order count from both maps
    for (const customer of customers) {
      // Count orders by customerId (preferred) and add any orders by phone without customerId
      const countById = customerIdToCountMap[customer.id] || 0;
      const countByPhone = phoneToCountMap[customer.phone] || 0;
      customer.totalOrders = countById + countByPhone;
    }

    return customers;
  } catch (error) {
    console.error('Error fetching customers:', error);
    return [];
  }
};

export const saveCustomer = async (customer: Customer): Promise<void> => {
  try {
    // Check if customer exists
    const existingCustomer = await query<Customer[]>(`
      SELECT * FROM customers WHERE id = ?
    `, [customer.id]);

    const isUpdate = existingCustomer && existingCustomer.length > 0;

    if (isUpdate) {
      // Update existing customer
      await query(`
        UPDATE customers
        SET firstName = ?, lastName = ?, phone = ?, email = ?,
            totalOrders = ?, lastOrderDate = ?
        WHERE id = ?
      `, [
        customer.firstName, customer.lastName, customer.phone, customer.email,
        customer.totalOrders, customer.lastOrderDate, customer.id
      ]);
    } else {
      // Insert new customer
      await query(`
        INSERT INTO customers
        (id, firstName, lastName, phone, email, totalOrders, lastOrderDate)
        VALUES (?, ?, ?, ?, ?, ?, ?)
      `, [
        customer.id, customer.firstName, customer.lastName, customer.phone,
        customer.email, customer.totalOrders, customer.lastOrderDate
      ]);
    }
  } catch (error) {
    console.error('Error saving customer:', error);
    throw error;
  }
};

export const deleteCustomer = async (id: string): Promise<void> => {
  try {
    await query(`DELETE FROM customers WHERE id = ?`, [id]);
  } catch (error) {
    console.error('Error deleting customer:', error);
    throw error;
  }
};

export const getProducts = async (): Promise<Product[]> => {
  try {
    const products = await query<Product[]>(`
      SELECT p.*, c.name as category 
      FROM products p
      JOIN categories c ON p.categoryId = c.id
      ORDER BY p.name
    `);
    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

export const saveProduct = async (product: Product): Promise<void> => {
  try {
    // Check if product exists
    const existingProduct = await query<Product[]>(`
      SELECT p.*, c.name as category 
      FROM products p
      JOIN categories c ON p.categoryId = c.id
      WHERE p.id = ?
    `, [product.id]);

    const isUpdate = existingProduct && existingProduct.length > 0;

    if (isUpdate) {
      // Update existing product
      await query(`
        UPDATE products
        SET name = ?, description = ?, price = ?, categoryId = ?, isAvailable = ?
        WHERE id = ?
      `, [
        product.name, product.description, product.price,
        product.categoryId, product.isAvailable, product.id
      ]);
    } else {
      // Insert new product
      await query(`
        INSERT INTO products
        (id, name, description, price, categoryId, isAvailable)
        VALUES (?, ?, ?, ?, ?, ?)
      `, [
        product.id, product.name, product.description,
        product.price, product.categoryId, product.isAvailable
      ]);
    }
  } catch (error) {
    console.error('Error saving product:', error);
    throw error;
  }
};

export const deleteProduct = async (id: string): Promise<void> => {
  try {
    await query(`DELETE FROM products WHERE id = ?`, [id]);
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

export const getCategories = async (): Promise<Category[]> => {
  try {
    const categories = await query<Category[]>(`
      SELECT * FROM categories
      ORDER BY name
    `);
    return categories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

export const saveCategory = async (cat: Category): Promise<void> => {
  try {
    // Check if category exists
    const existingCategory = await query<Category[]>(`
      SELECT * FROM categories WHERE id = ?
    `, [cat.id]);

    const isUpdate = existingCategory && existingCategory.length > 0;

    if (isUpdate) {
      // Update existing category
      await query(`
        UPDATE categories
        SET name = ?, description = ?
        WHERE id = ?
      `, [cat.name, cat.description, cat.id]);
    } else {
      // Insert new category
      await query(`
        INSERT INTO categories
        (id, name, description)
        VALUES (?, ?, ?)
      `, [cat.id, cat.name, cat.description]);
    }
  } catch (error) {
    console.error('Error saving category:', error);
    throw error;
  }
};

export const deleteCategory = async (id: string): Promise<void> => {
  try {
    await query(`DELETE FROM categories WHERE id = ?`, [id]);
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

// Update only a specific order item's status
export const updateOrderItemStatus = async (orderId: string, itemId: string, isDone: boolean): Promise<void> => {
  try {
    // Validate parameters
    if (!orderId || !itemId) {
      console.error('Missing required parameters:', { orderId, itemId });
      throw new Error('Missing required parameters');
    }

    // Update only the specific item's status
    await query(`
      UPDATE orderItems
      SET isDone = ?
      WHERE id = ? AND orderId = ?
    `, [isDone, itemId, orderId]);

    // Update the order's lastUpdated timestamp
    const currentTimestamp = generateEasternTimestamp();
    await query(`
      UPDATE orders
      SET lastUpdated = ?
      WHERE id = ?
    `, [currentTimestamp, orderId]);
  } catch (error) {
    console.error('Error updating order item status:', error);
    throw error;
  }
};

// Update only the order status and add a status history entry
export const updateOrderStatus = async (orderId: string, status: string, updatedBy: string): Promise<void> => {
  try {
    // Validate parameters
    if (!orderId || !status) {
      console.error('Missing required parameters:', { orderId, status });
      throw new Error('Missing required parameters');
    }

    // Generate current timestamp in Eastern timezone
    const currentTimestamp = generateEasternTimestamp();

    // Update only the order status and lastUpdated timestamp
    await query(`
      UPDATE orders
      SET status = ?, lastUpdated = ?
      WHERE id = ?
    `, [status, currentTimestamp, orderId]);

    // Add a new status history entry
    await query(`
      INSERT INTO statusHistory
      (orderId, status, timestamp, updatedBy)
      VALUES (?, ?, ?, ?)
    `, [orderId, status, currentTimestamp, updatedBy || 'System']);
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// Helper to update customer history when an order is saved
const updateCustomerFromOrder = async (order: Order): Promise<void> => {
  // Validate order object
  if (!order || typeof order !== 'object') {
    console.error('Invalid order object in updateCustomerFromOrder:', order);
    return;
  }

  // Validate required fields
  if (!order.phone) {
    console.error('Order missing phone number:', order);
    return;
  }

  // Use the order's createdAt timestamp or generate a new one in Eastern timezone
  const orderDate = order.createdAt || generateEasternTimestamp();

  try {
    // If order already has a customerId, use that to find the customer
    if (order.customerId) {
      const customerById = await query<Customer[]>(`
        SELECT * FROM customers WHERE id = ?
      `, [order.customerId]);

      if (customerById && Array.isArray(customerById) && customerById.length > 0) {
        const customer = customerById[0];

        if (!customer || typeof customer !== 'object' || !customer.id) {
          console.error('Invalid customer object:', customer);
          return;
        }

        // Update existing customer with Eastern timezone timestamp
        await query(`
          UPDATE customers
          SET firstName = ?, lastName = ?, lastOrderDate = ?, totalOrders = totalOrders + 1
          WHERE id = ?
        `, [
          order.firstName || '', order.lastName || '', orderDate, customer.id
        ]);

        return; // Customer found and updated by ID, no need to continue
      }
    }

    // If no customerId or customer not found by ID, check by phone number
    const existingCustomers = await query<Customer[]>(`
      SELECT * FROM customers WHERE phone = ?
    `, [order.phone]);

    if (existingCustomers && Array.isArray(existingCustomers) && existingCustomers.length > 0) {
      const customer = existingCustomers[0];

      if (!customer || typeof customer !== 'object' || !customer.id) {
        console.error('Invalid customer object:', customer);
        return;
      }

      // Update existing customer with Eastern timezone timestamp
      await query(`
        UPDATE customers
        SET firstName = ?, lastName = ?, lastOrderDate = ?, totalOrders = totalOrders + 1
        WHERE id = ?
      `, [
        order.firstName || '', order.lastName || '', orderDate, customer.id
      ]);

      // Update the order with the customer ID if it's not already set
      if (!order.customerId) {
        order.customerId = customer.id;
        await query(`
          UPDATE orders
          SET customerId = ?
          WHERE id = ?
        `, [customer.id, order.id]);
      }
    } else {
      // Create new customer with Eastern timezone timestamp
      const customerId = generateUUID();

      await query(`
        INSERT INTO customers
        (id, firstName, lastName, phone, email, totalOrders, lastOrderDate)
        VALUES (?, ?, ?, ?, ?, ?, ?)
      `, [
        customerId, 
        order.firstName || '', 
        order.lastName || '', 
        order.phone, 
        order.email || null, 
        1, 
        orderDate
      ]);

      // Update the order with the new customer ID
      order.customerId = customerId;
      await query(`
        UPDATE orders
        SET customerId = ?
        WHERE id = ?
      `, [customerId, order.id]);
    }
  } catch (error) {
    console.error('Error updating customer from order:', error);
    // Don't throw the error, just log it to prevent it from affecting the order save
  }
};
